import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { resultApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Badge, Table, Pager, Tabs } from "../../Components/UI/ui";
import { GATE, RESULT_STATUS_LABEL, fmtDateTime, todayStr, addDays } from "../../lib/lab";
import ResultSheet from "./ResultSheet";
import RadiologyWorklist from "../Radiology/RadiologyWorklist";

const VIEWS = [
  { id: "entry", label: "To enter", params: { status: "PENDING,ENTERED" } },
  { id: "signoff", label: "Sign-off queue", params: { status: "ENTERED" } },
  { id: "verified", label: "Verified", params: { status: "VERIFIED" } },
  { id: "critical", label: "Critical", params: { critical: true } },
  { id: "published", label: "Published", params: { status: "PUBLISHED" } },
  { id: "all", label: "All" },
];

// Pathology bench worklist over /labResults. List rows don't carry the template
// name, so templates are loaded once and joined by id.
const PathologyWorklist = () => {
  const { can } = useAuth();
  const { toast, templates, loadTemplates } = useApp();
  const [view, setView] = useState(can(...GATE.verifyResults) && !can(...GATE.templatesWrite) ? "signoff" : "entry");
  const [startDate, setStartDate] = useState(addDays(todayStr(), -14));
  const [endDate, setEndDate] = useState(todayStr());
  const [templateId, setTemplateId] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], totalCount: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25, ...(VIEWS.find((v) => v.id === view)?.params || {}) };
      if (view === "published" || view === "all") { params.startDate = startDate; params.endDate = endDate; }
      if (templateId) params.templateId = templateId;
      setData(await resultApi.list(params));
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [view, page, startDate, endDate, templateId, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [view, startDate, endDate, templateId]);

  const columns = [
    { key: "patientName", title: "Patient", render: (r) => (<><div className="primary">{r.patientName}</div><div className="sub">{r.patientContext?.sex || ""}{r.patientContext?.ageDays != null ? ` · ${Math.floor(r.patientContext.ageDays / 365)} y` : ""}</div></>) },
    { key: "templateId", title: "Test", render: (r) => { const t = templates[r.templateId]; return t ? (<><div>{t.name}</div><div className="sub">{t.shortName || ""}{t.category ? ` · ${t.category}` : ""}</div></>) : <span className="sub">{r.templateId}</span>; } },
    { key: "specimenLabel", title: "Sample", render: (r) => (<>
        <div style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>{r.specimenLabel || r.sampleId || <span className="sub">not collected</span>}</div>
        {r.sampleRejected && <Badge kind="rejected">Specimen rejected</Badge>}
      </>) },
    { key: "createdAt", title: "Ordered", render: (r) => fmtDateTime(r.createdAt) },
    { key: "flags", title: "Flags", render: (r) => (<span className="lab-row">
        {r.hasCritical && <Badge kind="CANCELLED">Critical</Badge>}
        {!r.hasCritical && r.hasAbnormal && <Badge kind="IN_PROCESS">Abnormal</Badge>}
        {!r.hasCritical && !r.hasAbnormal && r.status !== "PENDING" && <span className="sub">Normal</span>}
      </span>) },
    { key: "enteredBy", title: "Entered by", render: (r) => r.enteredBy?.name || <span className="sub">—</span> },
    { key: "verifiedBy", title: "Verified by", render: (r) => r.verifiedBy?.name || <span className="sub">—</span> },
    { key: "status", title: "Status", render: (r) => <Badge kind={r.status}>{RESULT_STATUS_LABEL[r.status]}</Badge> },
  ];

  return (
    <>
      <Tabs tabs={VIEWS} value={view} onChange={setView} />
      <div className="lab-toolbar">
        <select className="lab-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">All tests</option>
          {Object.values(templates).sort((a, b) => a.name.localeCompare(b.name)).map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
        {(view === "published" || view === "all") && (
          <>
            <input type="date" className="lab-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="lab-muted">to</span>
            <input type="date" className="lab-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table columns={columns} rows={data.results || []} loading={loading} onRowClick={(r) => setSelected(r._id)} empty="Nothing in this queue" />
      </div>
      <Pager page={page} totalPages={data.totalPages} total={data.totalCount} onChange={setPage} />

      <ResultSheet id={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </>
  );
};

// Results screen: pathology sheets and radiology studies side by side, since a
// lab that does both runs the two benches from the same place.
const Worklist = () => {
  const { branding, loadBranding } = useApp();
  const [params, setParams] = useSearchParams();
  const area = params.get("area") === "radiology" ? "radiology" : "pathology";

  useEffect(() => { loadBranding(); }, [loadBranding]);
  const rooms = branding?.branding?.radiologyRooms || [];

  return (
    <Page
      title="Results"
      subtitle={area === "radiology" ? "Scans: perform, image, report and release" : "Result sheets: enter values, verify and publish"}
    >
      <Tabs
        tabs={[{ id: "pathology", label: "Pathology" }, { id: "radiology", label: "Radiology" }]}
        value={area}
        onChange={(a) => setParams(a === "radiology" ? { area: "radiology" } : {})}
      />
      {area === "radiology" ? <RadiologyWorklist rooms={rooms} /> : <PathologyWorklist />}
    </Page>
  );
};

export default Worklist;
